import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useScript } from '../context/ScriptContext';
import { useAuth } from '../context/AuthContext';
import { ServiceItem } from '../types';
import * as api from '../services/api';
import { ApiError } from '../services/api';

const DEBOUNCE_MS = 500;

const LiturgySchedule: React.FC = () => {
  const { t } = useScript();
  const { isAdmin, token } = useAuth();

  const [services, setServices] = useState<ServiceItem[]>([]);
  const [liturgyNote, setLiturgyNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newServiceDay, setNewServiceDay] = useState('');
  const [newServiceTime, setNewServiceTime] = useState('');
  const [newServiceName, setNewServiceName] = useState('');
  const [newServiceImportant, setNewServiceImportant] = useState(false);

  // Stable ref for t() — avoids re-fetching data when translations arrive.
  const tRef = useRef(t);
  tRef.current = t;

  // Debounce timers for service updates and note saves.
  const updateTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const noteTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Keep a stable ref to services so debounced callbacks read the latest state.
  const servicesRef = useRef(services);
  servicesRef.current = services;

  // Cleanup timers on unmount — flush any pending saves.
  useEffect(() => {
    return () => {
      // Flush all pending service updates immediately on unmount.
      Object.values(updateTimers.current).forEach(clearTimeout);
      if (noteTimer.current) clearTimeout(noteTimer.current);
    };
  }, []);

  const loadSchedule = useCallback(() => {
    setLoading(true);
    setError(null);
    api.getServices()
      .then(items => { setServices(items); setLoading(false); })
      .catch((e: ApiError) => {
        setError(e?.message || tRef.current('Грешка при учитавању распореда.'));
        setLoading(false);
      });
    // The note is optional — silently ignore (it can legitimately 404)
    api.getSetting('liturgy_note').then(r => setLiturgyNote(r.value)).catch(() => {});
  }, []);

  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const addService = async () => {
    if (!newServiceTime.trim() || !newServiceName.trim() || !token) return;
    try {
      const created = await api.createService(token, {
        day: newServiceDay.trim(),
        time: newServiceTime.trim(),
        name: newServiceName.trim(),
        isImportant: newServiceImportant,
      });
      setServices(prev => [...prev, created]);
      setNewServiceDay('');
      setNewServiceTime('');
      setNewServiceName('');
      setNewServiceImportant(false);
    } catch {}
  };

  const deleteServiceHandler = async (id: number) => {
    if (!token || !window.confirm('Da li ste sigurni da želite da obrišete?')) return;
    try {
      await api.deleteService(token, id);
      setServices(prev => prev.filter(s => s.id !== id));
      // Cancel any pending debounced update for this item.
      if (updateTimers.current[id]) {
        clearTimeout(updateTimers.current[id]);
        delete updateTimers.current[id];
      }
    } catch {}
  };

  /**
   * Update a single field of a service item.
   * - Immediately updates local state (instant UI feedback).
   * - Debounces the API call (500ms) and sends the FULL item to avoid
   *   overwriting other fields with empty values.
   */
  const updateServiceHandler = (
    id: number,
    field: keyof ServiceItem,
    value: string | boolean
  ) => {
    // Update local state immediately for instant UI response.
    setServices(prev =>
      prev.map(s => (s.id === id ? { ...s, [field]: value } : s))
    );

    // Cancel any previous debounce for this item.
    if (updateTimers.current[id]) {
      clearTimeout(updateTimers.current[id]);
    }

    // Debounce: wait 500ms then send the FULL item to the server.
    updateTimers.current[id] = setTimeout(async () => {
      delete updateTimers.current[id];
      if (!token) return;
      // Read the latest state via ref (state may have changed since the timeout was set).
      const currentItem = servicesRef.current.find(s => s.id === id);
      if (!currentItem) return;
      try {
        await api.updateService(token, id, currentItem);
      } catch {}
    }, DEBOUNCE_MS);
  };

  /**
   * Update the liturgy note with debouncing.
   */
  const handleNoteChange = (value: string) => {
    setLiturgyNote(value);
    if (noteTimer.current) clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(async () => {
      if (!token) return;
      try {
        await api.updateSetting(token, 'liturgy_note', value);
      } catch {}
    }, DEBOUNCE_MS);
  };

  return (
    <section className="liturgy-container">
      <div className="liturgy-header">
        <h2>{t("Богослужбени Распоред")}</h2>
        <div style={{ color: '#c5a059', fontSize: '1.5rem' }}>{'\u2626'}</div>
      </div>

      {liturgyNote.trim() !== '' && (
        <p className="liturgy-note">{t(liturgyNote)}</p>
      )}

      {isAdmin && (
        <div className="liturgy-note-admin">
          <label className="admin-note-label" htmlFor="liturgy-admin-note">{t("Напомена испод наслова (опционо):")}</label>
          <textarea
            id="liturgy-admin-note"
            rows={2}
            className="admin-note-textarea"
            placeholder={t("Нпр. важи за наредни месец / током поста / ...")}
            value={liturgyNote}
            onChange={(e) => handleNoteChange(e.target.value)}
          />
          <small className="admin-note-help">
            {t("Ако оставите празно, напомена се неће приказивати.")}
          </small>
        </div>
      )}

      {loading && (
        <div className="state-block" role="status" aria-live="polite">
          <div className="state-spinner" aria-hidden="true" />
          <span>{t('Учитавање...')}</span>
        </div>
      )}

      {error && !loading && (
        <div className="state-block state-error" role="alert">
          <span>{error}</span>
          <button type="button" className="state-retry" onClick={loadSchedule}>
            {t('Покушај поново')}
          </button>
        </div>
      )}

      {!loading && !error && (
      <div className="liturgy-list">
        {services.map((service) => (
          <div key={service.id} className="liturgy-item">
            {isAdmin ? (
              <div className="admin-row">
                <input
                  type="text"
                  placeholder={t("дан (опционо)")}
                  aria-label={t("Дан")}
                  className="admin-input-day"
                  value={service.day}
                  onChange={(e) => updateServiceHandler(service.id, 'day', e.target.value)}
                />
                <input
                  type="text"
                  placeholder={t("време")}
                  aria-label={t("Време")}
                  className="admin-input-time"
                  value={service.time}
                  onChange={(e) => updateServiceHandler(service.id, 'time', e.target.value)}
                />
                <input
                  type="text"
                  aria-label={t("Назив службе")}
                  className="admin-input-name"
                  value={service.name}
                  onChange={(e) => updateServiceHandler(service.id, 'name', e.target.value)}
                />
                <label
                  className="admin-important"
                  title="Kada je uključeno, naziv službe se prikazuje crveno (važna liturgija/praznik)."
                >
                  <input
                    type="checkbox"
                    checked={service.isImportant}
                    onChange={(e) => updateServiceHandler(service.id, 'isImportant', e.target.checked)}
                  />
                  <span>{t("Важно")}</span>
                </label>
                <button onClick={() => deleteServiceHandler(service.id)} className="delete-btn" aria-label={t("Обриши")}>X</button>
              </div>
            ) : (
              <>
                <div className="liturgy-time-box">
                  {service.day.trim() !== '' && (
                    <div className="time-day">{t(service.day)}</div>
                  )}
                  <div className="time-hour">{t(service.time)}</div>
                </div>
                <div className={`liturgy-name ${service.isImportant ? 'important' : ''}`}>
                  {t(service.name)}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
      )}

      {isAdmin && (
        <div className="add-service-box">
          <h4 style={{ margin: '0 0 10px 0', color: '#8a1c1c' }}>
            {t("Додај ново богослужење:")}
          </h4>
          <div className="admin-row">
            <input
              type="text"
              placeholder={t("дан (опционо)")}
              aria-label={t("Дан")}
              className="admin-input-day"
              value={newServiceDay}
              onChange={(e) => setNewServiceDay(e.target.value)}
            />
            <input
              type="text"
              placeholder={t("време")}
              aria-label={t("Време")}
              className="admin-input-time"
              value={newServiceTime}
              onChange={(e) => setNewServiceTime(e.target.value)}
            />
            <input
              type="text"
              placeholder={t("Назив службе")}
              aria-label={t("Назив службе")}
              className="admin-input-name"
              value={newServiceName}
              onChange={(e) => setNewServiceName(e.target.value)}
            />
            <button onClick={addService} className="add-service-btn">DODAJ</button>
          </div>
          <div className="admin-important-block">
            <label className="admin-important">
              <input
                type="checkbox"
                checked={newServiceImportant}
                onChange={(e) => setNewServiceImportant(e.target.checked)}
              />
              <span>{t("Важна служба (празник / главна литургија)")}</span>
            </label>
            <small className="admin-important-help">
              {t("Када је ово укључено, назив службе ће бити приказан црвеном бојом како би се важне службе лакше уочиле.")}
            </small>
          </div>
        </div>
      )}
    </section>
  );
};

export default LiturgySchedule;

